// Pace-note data model and spoken-token vocabulary (convention: DECISIONS.md "Recon").
// Grade before direction ("four left"), 1–6 with 6 fastest, plus hairpin / open hairpin / acute /
// square and "flat" for flat-out kinks.

export type Grade = 'flat' | '6' | '5' | '4' | '3' | '2' | '1' | 'square' | 'acute' | 'hairpin' | 'open_hairpin';
export type Direction = 'left' | 'right';

export interface PaceNote {
  /** Index in the stage's note list. */
  id: number;
  /** Distance along the stage where the note's feature starts (turn-in for corners). */
  s: number;
  /** Distance where the feature ends (corner exit). */
  sEnd: number;
  kind: 'corner' | 'hazard' | 'stage';
  dir?: Direction;
  grade?: Grade;
  refine?: 'plus' | 'minus';
  length?: 'long' | 'very_long';
  shape?: 'tightens' | 'opens' | 'tightens_late';
  /** New grade when the corner tightens/opens through a band. */
  shapeGrade?: Grade;
  /** Modifiers spoken before the grade (hazards, crests, caution) and after (line). */
  pre: string[];
  post: string[];
  /** Link to the next note: a snapped distance ("100"), "and", "into", or none. */
  link?: string;
  /** Calibrated limit speed through the corner (m/s) — for calling and the limit bot. */
  limitSpeed?: number;
  minRadius?: number;
  angleDeg?: number;
  /** Spoken token sequence for this note (without the link). */
  tokens: string[];
  /** Short display text for subtitles. */
  text: string;
}

export interface StageNotes {
  stage: string;
  generator: string;
  convention: string;
  notes: PaceNote[];
}

export const GRADE_WORD: Record<Grade, string[]> = {
  flat: ['flat'],
  '6': ['six'],
  '5': ['five'],
  '4': ['four'],
  '3': ['three'],
  '2': ['two'],
  '1': ['one'],
  square: ['square'],
  acute: ['acute'],
  hairpin: ['hairpin'],
  open_hairpin: ['open', 'hairpin'],
};

export const DISTANCES = [30, 40, 50, 60, 70, 80, 100, 120, 150, 200, 250, 300, 400, 500];

export const DISTANCE_WORDS: Record<number, string[]> = {
  30: ['thirty'],
  40: ['forty'],
  50: ['fifty'],
  60: ['sixty'],
  70: ['seventy'],
  80: ['eighty'],
  100: ['one hundred'],
  120: ['one twenty'],
  150: ['one fifty'],
  200: ['two hundred'],
  250: ['two fifty'],
  300: ['three hundred'],
  400: ['four hundred'],
  500: ['five hundred'],
};

/** Modifier id → spoken words. */
export const MODIFIER_WORDS: Record<string, string[]> = {
  caution: ['caution'],
  double_caution: ['double caution'],
  triple_caution: ['triple caution'],
  over_crest: ['over', 'crest'],
  crest: ['crest'],
  jump: ['jump'],
  big_jump: ['big jump'],
  bump: ['bump'],
  dip: ['dip'],
  narrows: ['narrows'],
  bridge: ['bridge'],
  water_splash: ['water splash'],
  mud: ['mud'],
  loose: ['loose'],
  ruts: ['ruts'],
  potholes: ['potholes'],
  washout: ['washout'],
  rocks: ['rocks'],
  onto_tarmac: ['onto tarmac'],
  onto_gravel: ['onto gravel'],
  keep_in: ['keep in'],
  keep_out: ['keep out'],
  dont_cut: ["don't cut"],
  cut: ['cut'],
  long: ['long'],
  very_long: ['very long'],
  tightens: ['tightens'],
  tightens_late: ['tightens', 'late'],
  opens: ['opens'],
  widens: ['widens'],
  plus: ['plus'],
  minus: ['minus'],
  and: ['and'],
  into: ['into'],
};

/** Every spoken token the voice needs (each becomes one clip in the sprite). */
export const VOCABULARY: string[] = Array.from(
  new Set([
    ...Object.values(GRADE_WORD).flat(),
    'left',
    'right',
    ...Object.values(DISTANCE_WORDS).flat(),
    ...Object.values(MODIFIER_WORDS).flat(),
    // Stage calls and countdown.
    'start',
    'go',
    'five',
    'four',
    'three',
    'two',
    'one',
    'split',
    'finish',
    'flying finish',
    'stop',
    'stage',
    'good',
    'reset',
  ]),
);

/** Snap a gap (m) to the called distance, "and"/"into" for short gaps. */
export function snapDistance(gap: number): string {
  if (gap < 15) return 'into';
  if (gap < 30) return 'and';
  let best = DISTANCES[0];
  for (const d of DISTANCES) if (d <= gap + 4) best = d;
  return String(best);
}

export function linkWords(link: string | undefined): string[] {
  if (!link) return [];
  if (link === 'and' || link === 'into') return [link];
  const n = Number(link);
  return DISTANCE_WORDS[n] ?? [];
}

/** Display text for subtitles, e.g. "4 LEFT TIGHTENS 2 · DON'T CUT · 100". */
export function noteText(n: PaceNote): string {
  const parts: string[] = [];
  for (const m of n.pre) parts.push((MODIFIER_WORDS[m] ?? [m]).join(' '));
  if (n.kind === 'corner' && n.grade && n.dir) {
    const g = n.grade === 'open_hairpin' ? 'OPEN HAIRPIN' : n.grade.toUpperCase();
    parts.push(`${g} ${n.dir.toUpperCase()}${n.refine === 'plus' ? '+' : n.refine === 'minus' ? '−' : ''}`);
    if (n.length) parts.push(n.length === 'long' ? 'LONG' : 'VERY LONG');
    if (n.shape) parts.push(`${n.shape === 'tightens_late' ? 'TIGHTENS LATE' : n.shape.toUpperCase()}${n.shapeGrade ? ` ${n.shapeGrade.toUpperCase()}` : ''}`);
  }
  for (const m of n.post) parts.push((MODIFIER_WORDS[m] ?? [m]).join(' '));
  return parts.join(' · ').toUpperCase();
}

/** Spoken tokens for one note (without the trailing link). */
export function noteTokens(n: PaceNote): string[] {
  const t: string[] = [];
  for (const m of n.pre) t.push(...(MODIFIER_WORDS[m] ?? [m]));
  if (n.kind === 'corner' && n.grade && n.dir) {
    t.push(...GRADE_WORD[n.grade], n.dir);
    if (n.refine) t.push(n.refine);
    if (n.length) t.push(...MODIFIER_WORDS[n.length]);
    if (n.shape) {
      t.push(...MODIFIER_WORDS[n.shape]);
      if (n.shapeGrade) t.push(...GRADE_WORD[n.shapeGrade]);
    }
  }
  for (const m of n.post) t.push(...(MODIFIER_WORDS[m] ?? [m]));
  return t;
}
